import React from "react";

interface StatItem {
  label: string;
  value: string | number;
  delta?: string;
  tone?: "up" | "down" | "neutral";
}

const TONE_COLOR: Record<NonNullable<StatItem["tone"]>, string> = {
  up: "var(--c-success)",
  down: "var(--c-danger)",
  neutral: "var(--c-text-faint)",
};

function isStatItem(value: unknown): value is StatItem {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.label === "string" &&
    (typeof obj.value === "string" || typeof obj.value === "number")
  );
}

/**
 * KPI tile grid. Payload mirrors the workspace render-spec-compiler shape:
 *   { items: Array<{ label, value, delta?, tone? }> }
 *
 * Visual spec: see packages/design-system/src/sandbox/sandbox-styles.css
 * (.stat-card / .stat-value / .stat-label / .stat-change). The React block
 * uses design-token CSS variables to stay consistent with sandbox output.
 */
export default function StatGridBlock({ payload }: { payload: Record<string, unknown> }) {
  const rawItems = Array.isArray(payload.items) ? payload.items : [];
  const items = rawItems.filter(isStatItem);

  if (items.length === 0) {
    return (
      <div
        aria-label="stat-grid-empty"
        className="text-xs italic"
        style={{ color: "var(--c-text-faint)" }}
      >
        No stats to display.
      </div>
    );
  }

  const cols = items.length <= 2 ? 2 : items.length === 3 ? 3 : 4;

  return (
    <div
      aria-label="stat-grid"
      data-cols={cols}
      className="grid gap-3"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
    >
      {items.map((item, idx) => {
        const tone = item.tone ?? "neutral";
        return (
          <div
            key={`${item.label}-${idx}`}
            className="rounded-lg p-4"
            style={{
              backgroundColor: "var(--c-surface)",
              border: "1px solid var(--c-border)",
            }}
          >
            <div
              className="font-bold"
              style={{
                fontSize: 28,
                lineHeight: 1,
                letterSpacing: "-0.02em",
                color: "var(--c-text)",
              }}
            >
              {String(item.value)}
            </div>
            <div
              className="font-medium uppercase"
              style={{
                fontSize: 11,
                letterSpacing: "0.08em",
                color: "var(--c-text-faint)",
                marginTop: 6,
              }}
            >
              {item.label}
            </div>
            {item.delta && (
              <div
                data-tone={tone}
                className="font-medium"
                style={{
                  fontSize: 11,
                  marginTop: 4,
                  color: TONE_COLOR[tone],
                }}
              >
                {item.delta}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
